import { Colors, ConfirmDialog, Text } from '@amelio/ui-library';

import { getObjectivePeriodImpactMessage } from './objectivePeriodImpactMessage';

import type { ObjectivePeriod } from '@/types/objective-periods';

import { useDeleteObjectivePeriod } from '@/api/objective-periods';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';
import { getLocalizedText } from '@/utils/localization';

interface DeleteObjectivePeriodDialogProps {
    isOpen: boolean;
    period: ObjectivePeriod | null;
    onCancel: () => void;
    onSuccess: () => void;
}

/**
 * Warns before a period is deleted: the objectives attached to it keep their own dates and lose
 * the period, and the dialog says how many. It owns the mutation, unlike the deactivate and
 * complete confirmations, because deletion is the one action every period still offers -- a
 * completed period included.
 */
export const DeleteObjectivePeriodDialog = ({
    isOpen,
    period,
    onCancel,
    onSuccess,
}: DeleteObjectivePeriodDialogProps) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const deleteMutation = useDeleteObjectivePeriod();

    const handleDelete = async () => {
        if (!period) {
            return;
        }

        try {
            await deleteMutation.mutateAsync(period.id);
            showSuccessToast({ message: t('objectivePeriods.deleteSuccess') });
            onSuccess();
        } catch (error) {
            handleError({ error });
        }
    };

    return (
        <ConfirmDialog
            isOpen={isOpen}
            title={t('objectivePeriods.deleteTitle')}
            message={
                <div className={'flex flex-col gap-4'}>
                    <Text>
                        {t('objectivePeriods.deleteMessage', {
                            name: getLocalizedText(period?.name),
                        })}
                    </Text>

                    <Text
                        size={'small'}
                        color={Colors.BASE_600}
                    >
                        {getObjectivePeriodImpactMessage(t, period?.linkedObjectivesCount)}
                    </Text>
                </div>
            }
            confirmLabel={t('objectivePeriods.delete')}
            cancelLabel={t('objectivePeriods.cancel')}
            onConfirm={handleDelete}
            onCancel={onCancel}
            isLoading={deleteMutation.isPending}
            isDestructive={true}
            dataTestId={'delete-objective-period-dialog'}
        />
    );
};
